/**
 * TutorialOverlay.js - Lightweight Interactive Tutorial System
 * 
 * Features:
 * - Semi-transparent overlay with highlighted target
 * - Floating tutorial card with progress indicator
 * - Persists state to database
 * - Reusable across SmartBalloon and SmartRevision
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { X, ChevronRight, HelpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Main Tutorial Overlay Component
 */
const TutorialOverlay = ({
  tutorialId,
  steps,
  isOpen,
  onClose,
  onComplete,
  token,
  startStep = 0  // Allow starting from a specific step
}) => {
  const [currentStep, setCurrentStep] = useState(startStep);
  const [targetRect, setTargetRect] = useState(null);
  const [cardPosition, setCardPosition] = useState({ top: '50%', left: '50%' });
  
  // Reset to startStep when it changes or when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(startStep);
    }
  }, [isOpen, startStep]);
  
  const step = steps[currentStep];
  
  // Calculate target element position
  useEffect(() => {
    if (!isOpen || !step?.targetSelector) return;
    
    const updatePosition = () => {
      const target = document.querySelector(step.targetSelector);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect({
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16
        });
        
        // Position card near target but not overlapping
        const cardWidth = 320;
        const cardHeight = 180;
        const padding = 20;
        
        let cardTop = rect.bottom + padding;
        let cardLeft = rect.left;
        
        // Adjust if card would go off screen
        if (cardTop + cardHeight > window.innerHeight - padding) {
          cardTop = rect.top - cardHeight - padding;
        }
        if (cardLeft + cardWidth > window.innerWidth - padding) {
          cardLeft = window.innerWidth - cardWidth - padding;
        }
        if (cardLeft < padding) {
          cardLeft = padding;
        }
        if (cardTop < padding) {
          cardTop = padding;
        }
        
        setCardPosition({ top: `${cardTop}px`, left: `${cardLeft}px` });
      } else {
        // Center if target not found
        setTargetRect(null);
        setCardPosition({ 
          top: '50%', 
          left: '50%',
          transform: 'translate(-50%, -50%)'
        });
      }
    };
    
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);
    
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [isOpen, step, currentStep]);
  
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };
  
  const handleSkip = () => {
    handleComplete();
  };
  
  const handleComplete = async () => {
    // Save completion to database
    if (token && tutorialId) {
      try {
        await fetch(`${API_URL}/api/user/tutorials`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ tutorial_id: tutorialId, completed: true })
        });
      } catch (e) {
        console.error('Failed to save tutorial state:', e);
      }
    }
    
    // Also save to localStorage as fallback
    const tutorials = JSON.parse(localStorage.getItem('tutorials_completed') || '{}');
    tutorials[tutorialId] = true;
    localStorage.setItem('tutorials_completed', JSON.stringify(tutorials));
    
    onComplete?.();
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[200] pointer-events-auto">
      {/* Dark overlay with hole for target */}
      <div className="absolute inset-0 bg-black/60 transition-opacity duration-300">
        {targetRect && (
          <div 
            className="absolute bg-transparent rounded-lg ring-4 ring-blue-400 ring-opacity-80 shadow-[0_0_0_9999px_rgba(0,0,0,0.6)]"
            style={{
              top: targetRect.top,
              left: targetRect.left,
              width: targetRect.width,
              height: targetRect.height,
              transition: 'all 0.3s ease-out'
            }}
          />
        )}
      </div>
      
      {/* Tutorial Card */}
      <div 
        className="absolute bg-white rounded-xl shadow-2xl p-5 w-80 animate-in fade-in slide-in-from-bottom-4 duration-300"
        style={cardPosition}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <HelpCircle className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-slate-500">
              {currentStep + 1} / {steps.length}
            </span>
          </div>
          <button 
            onClick={handleSkip}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Progress bar */}
        <div className="h-1 bg-slate-100 rounded-full mb-4 overflow-hidden">
          <div 
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
        
        {/* Content */}
        <div className="mb-5">
          {step?.icon && (
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-3">
              {step.icon}
            </div>
          )}
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            {step?.title}
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            {step?.description}
          </p>
        </div>
        
        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
          >
            Salta
          </button>
          <Button 
            onClick={handleNext}
            size="sm"
            className="gap-1"
          >
            {currentStep < steps.length - 1 ? (
              <>
                Avanti
                <ChevronRight className="w-4 h-4" />
              </>
            ) : (
              'Fine'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Contextual Tooltip Component (for Guided View Comparison)
 */
export const ContextualTooltip = ({
  id,
  children,
  content,
  position = 'bottom',
  showOnce = true
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  
  useEffect(() => {
    if (showOnce) {
      const dismissed = JSON.parse(localStorage.getItem('tooltips_dismissed') || '{}');
      if (dismissed[id]) {
        setIsDismissed(true);
      }
    }
  }, [id, showOnce]);
  
  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    if (showOnce) {
      const dismissed = JSON.parse(localStorage.getItem('tooltips_dismissed') || '{}');
      dismissed[id] = true;
      localStorage.setItem('tooltips_dismissed', JSON.stringify(dismissed));
    }
  };
  
  useEffect(() => {
    if (!isDismissed) {
      const timer = setTimeout(() => setIsVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, [isDismissed]);
  
  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };
  
  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-slate-800',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-slate-800',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-slate-800',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-slate-800'
  };
  
  return (
    <div className="relative inline-block">
      {children}
      
      {isVisible && !isDismissed && (
        <div 
          className={`absolute z-50 ${positionClasses[position]} animate-in fade-in slide-in-from-bottom-2 duration-200`}
        >
          <div className="bg-slate-800 text-white text-sm px-3 py-2 rounded-lg shadow-lg max-w-xs">
            <div className="flex items-start gap-2">
              <HelpCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="leading-relaxed">{content}</p>
            </div>
            <button
              onClick={handleDismiss}
              className="absolute -top-1 -right-1 w-5 h-5 bg-slate-700 hover:bg-slate-600 rounded-full flex items-center justify-center transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          {/* Arrow */}
          <div className={`absolute w-0 h-0 border-4 border-transparent ${arrowClasses[position]}`} />
        </div>
      )}
    </div>
  );
};

/**
 * Hook to check if tutorial should be shown
 */
export const useTutorial = (tutorialId, token) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const checkTutorialStatus = async () => {
      // Check localStorage first (fast)
      const localTutorials = JSON.parse(localStorage.getItem('tutorials_completed') || '{}');
      if (localTutorials[tutorialId]) {
        setShouldShow(false);
        setIsLoading(false);
        return;
      }
      
      // Check database if token available
      if (token) {
        try {
          const res = await fetch(`${API_URL}/api/user/tutorials`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            const completed = data.tutorials_completed || {};
            if (completed[tutorialId]) {
              // Sync to localStorage
              localStorage.setItem('tutorials_completed', JSON.stringify({
                ...localTutorials,
                [tutorialId]: true
              }));
              setShouldShow(false);
              setIsLoading(false);
              return;
            }
          }
        } catch (e) {
          console.error('Failed to check tutorial status:', e);
        }
      }
      
      setShouldShow(true);
      setIsLoading(false);
    };
    
    checkTutorialStatus();
  }, [tutorialId, token]);
  
  const resetTutorial = () => {
    const localTutorials = JSON.parse(localStorage.getItem('tutorials_completed') || '{}');
    delete localTutorials[tutorialId];
    localStorage.setItem('tutorials_completed', JSON.stringify(localTutorials));
    setShouldShow(true);
  };
  
  return { shouldShow, isLoading, resetTutorial };
};

/**
 * Help Menu Button with Reset Tutorial option
 */
export const HelpMenuButton = ({ onResetTutorial }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-full hover:bg-slate-100 transition-colors"
        title="Aiuto"
      >
        <HelpCircle className="w-5 h-5 text-slate-500" />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50 min-w-[180px]">
            <button
              onClick={() => {
                onResetTutorial?.();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
            >
              <HelpCircle className="w-4 h-4" />
              Rivedi tutorial
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default TutorialOverlay;
